/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  Action creators for the Filters module
 */

import type { AIAssistantLocationResponse, Filters } from '@zcode/state-type'
import { createAction } from 'redux-actions'
import type { FILTER_MODAL_TYPES } from '../../../constants/filterConstants'
import {
  CANCEL_FETCHING_LISTABLES_COUNT,
  DONE_FETCHING_LISTABLES_COUNT,
  DONE_FETCHING_MATCHING_COUNT,
  DONE_FETCHING_PROPERTY_TYPE_COUNTS,
  ERROR_FETCHING_LISTABLES_COUNT,
  RECEIVE_LISTABLES_COUNT,
  RESET_FILTERS,
  RESET_NON_ST_FILTERS,
  SET_FILTERS_CHANGED,
  SET_FILTER_MATCHING_COUNT,
  SET_FILTER_MODAL_TYPE,
  SET_IS_FILTER_MODAL_OPEN,
  SET_IS_ZOE_SEARCH,
  SET_PROPERTY_TYPE_COUNTS,
  SET_SHORT_TERM_CALENDAR_OPEN,
  SET_ZOE_LOCATION,
  START_FETCHING_LISTABLES_COUNT,
  START_FETCHING_MATCHING_COUNT,
  START_FETCHING_PROPERTY_TYPE_COUNTS,
  UPDATE_FILTERS,
} from '../constants'

export const cancelFetchingListablesCount = createAction<
  string,
  typeof CANCEL_FETCHING_LISTABLES_COUNT
>(CANCEL_FETCHING_LISTABLES_COUNT)
export const doneFetchingListablesCount = createAction<
  string,
  typeof DONE_FETCHING_LISTABLES_COUNT
>(DONE_FETCHING_LISTABLES_COUNT)
export const errorFetchingListablesCount = createAction<
  unknown,
  typeof ERROR_FETCHING_LISTABLES_COUNT
>(ERROR_FETCHING_LISTABLES_COUNT)
export const receiveListablesCount = createAction<number, typeof RECEIVE_LISTABLES_COUNT>(
  RECEIVE_LISTABLES_COUNT
)
export const resetFilters = createAction(RESET_FILTERS)
export const resetNonSTFilters = createAction<Filters, typeof RESET_NON_ST_FILTERS>(
  RESET_NON_ST_FILTERS
)
export const startFetchingListablesCount = createAction<
  string,
  typeof START_FETCHING_LISTABLES_COUNT
>(START_FETCHING_LISTABLES_COUNT)
export const updateFilters = createAction<
  { filters: Filters; replace: true } | { filters: Partial<Filters>; replace?: false },
  typeof UPDATE_FILTERS
>(UPDATE_FILTERS)

// FILTER MODAL
export const setFilterModalType = createAction<
  (typeof FILTER_MODAL_TYPES)[keyof typeof FILTER_MODAL_TYPES],
  typeof SET_FILTER_MODAL_TYPE
>(SET_FILTER_MODAL_TYPE)
export const setIsFilterModalOpen = createAction<boolean, typeof SET_IS_FILTER_MODAL_OPEN>(
  SET_IS_FILTER_MODAL_OPEN
)
export const setShortTermCalendarPickerOpen = createAction<
  boolean,
  typeof SET_SHORT_TERM_CALENDAR_OPEN
>(SET_SHORT_TERM_CALENDAR_OPEN)
export const setFiltersChanged = createAction<boolean, typeof SET_FILTERS_CHANGED>(
  SET_FILTERS_CHANGED
)

// MATCHING COUNT
export const setFilterMatchingCount = createAction<number, typeof SET_FILTER_MATCHING_COUNT>(
  SET_FILTER_MATCHING_COUNT
)
export const startFetchingMatchingCount = createAction(START_FETCHING_MATCHING_COUNT)
export const doneFetchingMatchingCount = createAction(DONE_FETCHING_MATCHING_COUNT)

// PROPERTY_TYPE_COUNTS
export const setPropertyTypeCounts = createAction<
  { [key: string]: number },
  typeof SET_PROPERTY_TYPE_COUNTS
>(SET_PROPERTY_TYPE_COUNTS)
export const startFetchingPropertyTypeCounts = createAction(START_FETCHING_PROPERTY_TYPE_COUNTS)
export const doneFetchingPropertyTypeCounts = createAction(DONE_FETCHING_PROPERTY_TYPE_COUNTS)

// ZOE SEARCH
export const setIsZoeSearch = createAction<boolean, typeof SET_IS_ZOE_SEARCH>(SET_IS_ZOE_SEARCH)
export const setZoeLocation = createAction<
  AIAssistantLocationResponse | null,
  typeof SET_ZOE_LOCATION
>(SET_ZOE_LOCATION)
